import {
  type Aircraft,
  createAircraft,
  cruiseSpeed,
  landingProgress,
  takeoffProgress,
} from "../aircraft/Aircraft";
import type {
  AirportMap,
  MutableVec2,
  Vec2,
  WorldBounds,
} from "../airport/AirportDefinition";
import { COMMONEST_AIRFRAME_WEIGHT } from "../aircraft/Airframes";
import { FEEDBACK, type GameplayConfig, SCORING } from "../config/gameplay";
import type { RunwayColor } from "../config/palette";
import { ScoreSystem } from "../scoring/ScoreSystem";
import { findCollision, proximityLevels } from "../simulation/CollisionSystem";
import {
  clonePoint,
  distance,
  pointToSegmentDistance,
} from "../simulation/geometry";
import { advanceAlongPath } from "../simulation/MovementSystem";
import {
  buildApproachPath,
  type LandingIntent,
  landingIntentFor,
  simplifyPath,
} from "../simulation/RouteSystem";
import { isRunwayClaimed } from "../simulation/RunwaySystem";
import { isOutsideBounds, type SpawnWarning } from "../simulation/SpawnSystem";
import {
  type DepartureContext,
  DepartureScheduler,
} from "./DepartureScheduler";
import type { GameEvent } from "./events";
import { type ReplayFrame, ReplayRecorder } from "./ReplayRecorder";
import { type SpawnContext, SpawnDirector } from "./SpawnDirector";
import { SeededRandom } from "./SeededRandom";
import type { GamePhase, GameSnapshot, RunwayStatus } from "./snapshot";

/** Pick radii, widened for touch input. */
export interface PickRadii {
  readonly aircraft?: number;
  readonly path?: number;
}

export interface DrawingState {
  readonly aircraft: Aircraft;
  readonly points: MutableVec2[];
}

export type ReleaseOutcome = "landing" | "rejected" | "path" | null;

export interface GameSessionOptions {
  readonly map: AirportMap;
  readonly gameplay: GameplayConfig;
  readonly bounds?: WorldBounds;
  readonly seed?: number;
}

const DEFAULT_BOUNDS: WorldBounds = {
  minX: -50,
  maxX: 50,
  minY: -50,
  maxY: 50,
};

/**
 * The whole game.
 *
 * Steps at a fixed 60 Hz off an accumulator so physics never depends on frame
 * rate, and exposes the pointer verbs (`grab` / `drag` / `release`) the input
 * layer drives directly.
 */
export class GameSession {
  readonly map: AirportMap;
  readonly gameplay: GameplayConfig;

  phase: GamePhase = "playing";
  bounds: WorldBounds = DEFAULT_BOUNDS;
  aircraft: Aircraft[] = [];
  private readonly spawns: SpawnDirector;
  drawing: DrawingState | null = null;
  crashPair: readonly [Aircraft, Aircraft] | null = null;
  warningIds = new Set<number>();
  /**
   * Per-aircraft closeness of the worst near miss, 0 at the edge of the
   * warning radius and 1 at contact. Presentation only — the ring is drawn
   * from it, nothing in the step reads it.
   */
  warningLevels: ReadonlyMap<number, number> = new Map();
  hoveredId: number | null = null;

  private readonly score = new ScoreSystem();
  private random: SeededRandom;
  private currentSeed: number;
  private readonly departures = new DepartureScheduler();
  // Presentation only: nothing in `step` reads it back, so it cannot move an
  // outcome, and it draws no random numbers.
  private readonly replay = new ReplayRecorder(
    FEEDBACK.replaySeconds,
    1 / FEEDBACK.replaySampleHz,
  );

  private elapsed = 0;
  private accumulator = 0;
  private traffic = new Map<RunwayColor, { occupied: number }>();
  private events: GameEvent[] = [];

  constructor(options: GameSessionOptions) {
    this.map = options.map;
    this.gameplay = options.gameplay;
    this.currentSeed = options.seed ?? Date.now();
    this.random = new SeededRandom(this.currentSeed);
    this.spawns = new SpawnDirector(this.gameplay);
    this.reset(options.bounds ?? DEFAULT_BOUNDS);
  }

  get elapsedSeconds(): number {
    return this.elapsed;
  }

  /**
   * The seed this run is playing from.
   *
   * Recorded with a submitted score, so a run can be replayed and checked
   * later. That is only worth anything if the seed really does determine the
   * run, which is why `reset` takes a new one — see below.
   */
  get seed(): number {
    return this.currentSeed;
  }

  /**
   * Start over.
   *
   * A seed given here re-seeds the generator, so the next run is reproducible
   * from it. Without that the second run of a session shares the first one's
   * seed while continuing its generator state, and a recorded seed would name
   * a run nobody could reproduce. Left out, the generator carries on — which
   * is what the tests that reset mid-scenario expect.
   */
  reset(bounds: WorldBounds = this.bounds, seed?: number): void {
    if (seed !== undefined) {
      this.currentSeed = seed;
      this.random = new SeededRandom(seed);
    }
    this.bounds = bounds;
    this.aircraft = [];
    this.spawns.reset();
    this.score.reset();
    this.elapsed = 0;
    this.accumulator = 0;
    this.departures.reset();
    this.replay.reset();
    this.drawing = null;
    this.crashPair = null;
    this.warningIds = new Set();
    this.warningLevels = new Map();
    this.hoveredId = null;
    this.events = [];
    this.traffic = new Map([
      ["yellow", { occupied: 0 }],
      ["blue", { occupied: 0 }],
      ["red", { occupied: 0 }],
    ]);
    this.phase = "playing";
  }

  setBounds(bounds: WorldBounds): void {
    this.bounds = bounds;
  }

  /** Pending arrivals, telegraphed on the edge of the world. */
  get spawnWarnings(): readonly SpawnWarning[] {
    return this.spawns.warnings;
  }

  get spawningEnabled(): boolean {
    return this.spawns.enabled;
  }

  setSpawningEnabled(enabled: boolean): void {
    this.spawns.setEnabled(enabled);
  }

  pause(): void {
    if (this.phase === "playing") {
      this.phase = "paused";
    }
  }

  resume(): void {
    if (this.phase === "paused") {
      this.phase = "playing";
    }
  }

  togglePause(): void {
    if (this.phase === "playing") {
      this.phase = "paused";
    } else if (this.phase === "paused") {
      this.phase = "playing";
    }
  }

  forceGameOver(position: Vec2 = { x: 0, y: 0 }): void {
    if (this.phase === "gameover") {
      return;
    }
    this.events.push({
      type: "crash",
      position: clonePoint(position),
      immediate: true,
    });
    this.phase = "gameover";
  }

  /** Feed a frame delta in milliseconds. */
  update(deltaMs: number): void {
    if (this.phase !== "playing") {
      return;
    }
    this.accumulator += Math.min(deltaMs / 1000, this.gameplay.maxFrameSeconds);
    const step = this.gameplay.fixedStepSeconds;
    while (this.accumulator >= step) {
      this.step(step);
      this.accumulator -= step;
      if (this.phase !== "playing") {
        break;
      }
    }
  }

  step(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;

    if (this.spawns.enabled) {
      this.admitArrivals(deltaSeconds);
    }
    this.spawns.schedule(deltaSeconds, this.spawnContext());

    for (const aircraft of this.aircraft) {
      if (aircraft.state === "parked") {
        continue;
      }
      aircraft.age += deltaSeconds;
      this.stepAircraft(aircraft, deltaSeconds);
      if (
        !aircraft.enteredView &&
        !isOutsideBounds(aircraft.pos, this.bounds, 0)
      ) {
        aircraft.enteredView = true;
      }
    }

    this.cullAircraft();

    const collisionOptions = {
      graceSeconds: this.gameplay.collisionGraceSeconds,
      departureAirborneProgress: this.gameplay.departureAirborneProgress,
      warningFactor: this.gameplay.warningFactor,
    };
    const previousWarnings = this.warningIds;
    // One pass: the ids the renderer rings are the keys of the severity map.
    this.warningLevels = proximityLevels(this.aircraft, collisionOptions);
    this.warningIds = new Set(this.warningLevels.keys());

    // Only the step a ring first appears, so the alert does not repeat for as
    // long as the pair stays close.
    const newlyWarned = this.aircraft.find(
      (aircraft) =>
        this.warningIds.has(aircraft.id) && !previousWarnings.has(aircraft.id),
    );
    if (newlyWarned) {
      this.events.push({
        type: "near-miss",
        position: clonePoint(newlyWarned.pos),
      });
      // Charged once per encounter, on the step the ring appears, for the same
      // reason the alert is: a pair that stays close is one mistake.
      this.score.penalise(SCORING.nearMissPenalty);
      this.breakStreak(newlyWarned.pos);
    }

    const collision = findCollision(this.aircraft, collisionOptions);
    if (collision) {
      this.crashPair = collision;
      this.events.push({
        type: "crash",
        position: {
          x: (collision[0].pos.x + collision[1].pos.x) / 2,
          y: (collision[0].pos.y + collision[1].pos.y) / 2,
        },
        aircraftIds: [collision[0].id, collision[1].id],
      });
      this.phase = "gameover";
    }

    // Last thing in the step, so the frame a run ends on is the one the
    // replay finishes on.
    this.replay.record(this.elapsed, this.aircraft, this.phase !== "playing");
  }

  /** The last few seconds of the board, oldest frame first. */
  replayFrames(): readonly ReplayFrame[] {
    return this.replay.window;
  }

  /**
   * End the run of clean landings, if there is one.
   *
   * A refused drop and a lost separation both count: the streak is about
   * traffic the player kept clean, not only about landings.
   */
  private breakStreak(position: Vec2): void {
    const lost = this.score.breakStreak();
    if (lost > 0) {
      this.events.push({
        type: "streak-break",
        position: clonePoint(position),
        streak: lost,
      });
    }
  }

  /** Read-only view handed to the spawn director. */
  private spawnContext(): SpawnContext {
    return {
      bounds: this.bounds,
      aircraft: this.aircraft,
      elapsed: this.elapsed,
      random: () => this.random.next(),
    };
  }

  /** Turn the arrivals the director released into live aircraft. */
  private admitArrivals(deltaSeconds: number): void {
    for (const arriving of this.spawns.advance(
      deltaSeconds,
      this.spawnContext(),
    )) {
      const aircraft = createAircraft(
        arriving.kind,
        arriving.pos,
        arriving.heading,
        arriving.airframe,
      );
      this.aircraft.push(aircraft);
      this.events.push({
        type: "spawn",
        position: clonePoint(aircraft.pos),
        color: aircraft.kind,
      });
    }
  }

  private stepAircraft(aircraft: Aircraft, deltaSeconds: number): void {
    if (aircraft.state === "taxiing-in") {
      advanceAlongPath(aircraft, deltaSeconds, this.gameplay.taxiInSpeed);
      if (aircraft.path.length === 0) {
        this.queueDeparture(aircraft);
      }
      return;
    }

    if (aircraft.state === "taxiing-out") {
      advanceAlongPath(aircraft, deltaSeconds, this.gameplay.taxiOutSpeed);
      if (aircraft.path.length === 0) {
        aircraft.state = "holding";
      }
      if (aircraft.state === "holding" && this.departureIsDue(aircraft)) {
        this.tryLaunchDeparture(aircraft);
      }
      return;
    }

    if (aircraft.state === "holding") {
      if (this.departureIsDue(aircraft)) {
        this.tryLaunchDeparture(aircraft);
      }
      return;
    }

    if (aircraft.state === "departing") {
      advanceAlongPath(aircraft, deltaSeconds, this.gameplay.departureSpeed);
      if (aircraft.target) {
        aircraft.takeoffProgress = takeoffProgress(aircraft);
      }
      return;
    }

    advanceAlongPath(aircraft, deltaSeconds, cruiseSpeed(aircraft));
    if (!aircraft.target) {
      return;
    }

    if (
      aircraft.state === "flying" &&
      aircraft.path.length <= 1 &&
      distance(aircraft.pos, aircraft.target.approach) <=
        this.gameplay.landingCaptureDistance
    ) {
      aircraft.state = "landing";
      const slot = this.traffic.get(aircraft.target.color);
      if (slot) {
        slot.occupied++;
      }
      const award = this.score.creditLanding({
        airframeWeight: aircraft.airframe.weight,
        commonestWeight: COMMONEST_AIRFRAME_WEIGHT,
        airborne: this.airborneCount(),
        ageSeconds: aircraft.age,
      });
      this.events.push({
        type: "land",
        position: clonePoint(aircraft.pos),
        color: aircraft.target.color,
        heading: aircraft.heading,
        total: award.total,
        points: award.points,
      });
    }

    aircraft.landProgress = landingProgress(aircraft);

    if (aircraft.path.length === 0) {
      const arrival = this.map.groundRoutes[aircraft.target.id]?.arrival;
      if (arrival && arrival.length > 0) {
        aircraft.state = "taxiing-in";
        aircraft.path = arrival.map(clonePoint);
      } else {
        this.queueDeparture(aircraft);
      }
    }
  }

  private cullAircraft(): void {
    this.aircraft = this.aircraft.filter((aircraft) => {
      const outside = isOutsideBounds(
        aircraft.pos,
        this.bounds,
        this.gameplay.despawnMargin,
      );
      if (aircraft.state === "departing") {
        return !outside;
      }
      if (aircraft.target || !outside) {
        return true;
      }
      // An arrival is born outside the view and is only litter once it has
      // been in shot and left again. The age cap is the backstop for one that
      // never manages it — a resize can move the view out from under it.
      return (
        !aircraft.enteredView &&
        aircraft.age <= this.gameplay.arrivalGraceSeconds
      );
    });

    // `grab` clears the clearance, which is exactly what makes an aircraft
    // eligible for the sweep above — so the gesture can outlive its subject.
    if (this.drawing && !this.aircraft.includes(this.drawing.aircraft)) {
      this.drawing = null;
    }
    if (
      this.hoveredId !== null &&
      !this.aircraft.some((aircraft) => aircraft.id === this.hoveredId)
    ) {
      this.hoveredId = null;
    }
  }

  // ---------------------------------------------------------------- pointer

  findAircraftAt(
    point: Vec2,
    radius = this.gameplay.aircraftPickRadius,
  ): Aircraft | null {
    if (this.phase !== "playing") {
      return null;
    }
    let closest: Aircraft | null = null;
    let closestDistance = radius;
    for (const aircraft of this.aircraft) {
      if (aircraft.state !== "flying") {
        continue;
      }
      const gap = distance(point, aircraft.pos);
      if (gap <= closestDistance) {
        closest = aircraft;
        closestDistance = gap;
      }
    }
    return closest;
  }

  findPathAt(
    point: Vec2,
    radius = this.gameplay.pathPickRadius,
  ): Aircraft | null {
    if (this.phase !== "playing") {
      return null;
    }
    let closest: Aircraft | null = null;
    let closestDistance = radius;
    for (const aircraft of this.aircraft) {
      if (aircraft.state !== "flying" || aircraft.path.length === 0) {
        continue;
      }
      const track = [aircraft.pos, ...aircraft.path];
      for (let i = 0; i < track.length - 1; i++) {
        const gap = pointToSegmentDistance(point, track[i]!, track[i + 1]!);
        if (gap <= closestDistance) {
          closest = aircraft;
          closestDistance = gap;
        }
      }
    }
    return closest;
  }

  hover(point: Vec2, radii: PickRadii = {}): number | null {
    const found =
      this.findAircraftAt(point, radii.aircraft) ??
      this.findPathAt(point, radii.path);
    this.hoveredId = found?.id ?? null;
    return this.hoveredId;
  }

  /** Start drawing: clears the aircraft's clearance and existing path. */
  grab(point: Vec2, radii: PickRadii = {}): boolean {
    if (this.phase !== "playing") {
      return false;
    }
    const found =
      this.findAircraftAt(point, radii.aircraft) ??
      this.findPathAt(point, radii.path);
    if (!found) {
      return false;
    }
    found.target = null;
    found.path = [];
    this.hoveredId = found.id;
    this.drawing = { aircraft: found, points: [clonePoint(found.pos)] };
    return true;
  }

  drag(point: Vec2): boolean {
    // The canvas holds pointer capture, so moves keep arriving through the
    // game-over and pause overlays. Drop the gesture instead of drawing on a
    // run that is no longer running.
    if (this.phase !== "playing") {
      this.drawing = null;
      return false;
    }
    if (!this.drawing) {
      return false;
    }
    const points = this.drawing.points;
    const last = points[points.length - 1]!;
    if (distance(point, last) < this.gameplay.dragSampleDistance) {
      return false;
    }
    points.push(clonePoint(point));
    this.drawing.aircraft.path.push(clonePoint(point));
    return true;
  }

  release(): ReleaseOutcome {
    const drawing = this.drawing;
    this.drawing = null;
    if (!drawing || this.phase !== "playing") {
      return null;
    }

    const { aircraft, points } = drawing;

    // A tap is not a route. `grab` seeds `points` with the aircraft's own
    // position, so evaluating an intent here would land — or noisily reject —
    // any aircraft that merely loitered within snap distance of a threshold,
    // for a gesture that never drew a ribbon.
    if (points.length < 2) {
      return null;
    }

    const last = points[points.length - 1]!;
    const intent = this.landingIntentFor(aircraft, last);

    if (intent?.status === "eligible") {
      aircraft.target = intent.runway;
      aircraft.path = buildApproachPath(
        aircraft.pos,
        aircraft.path,
        intent.runway.approach,
        intent.runway.end,
        this.gameplay.runwaySnapDistance,
        this.gameplay.pathSimplifyDistance,
      );
      return "landing";
    }

    aircraft.path = simplifyPath(
      aircraft.path,
      this.gameplay.pathSimplifyDistance,
    );

    if (intent) {
      const position =
        intent.status === "direction"
          ? intent.runway.end
          : intent.runway.approach;
      this.events.push({
        type: "reject",
        position: clonePoint(position),
        reason: intent.status,
      });
      // A busy runway is a "not yet", not a mistake: the player read the
      // colour and the direction correctly, and at the traffic this refusal
      // exists to create it would fire often enough that no streak could ever
      // survive. The wrong runway still costs the streak.
      if (intent.status !== "occupied") {
        this.breakStreak(position);
      }
    }

    return intent ? "rejected" : "path";
  }

  /** Live intent for the path being drawn, used for the runway highlight. */
  getLandingIntent(): LandingIntent | null {
    if (!this.drawing) {
      return null;
    }
    const points = this.drawing.points;
    return this.landingIntentFor(
      this.drawing.aircraft,
      points[points.length - 1]!,
    );
  }

  private landingIntentFor(
    aircraft: Aircraft,
    point: Vec2,
  ): LandingIntent | null {
    return landingIntentFor(
      aircraft,
      point,
      this.map.runways,
      this.gameplay.runwaySnapDistance,
      (runway) => isRunwayClaimed(runway.id, this.aircraft, aircraft),
    );
  }

  // -------------------------------------------------------------- departures

  /** Read-only view of the session, handed to the scheduler per call. */
  private departureContext(): DepartureContext {
    return {
      map: this.map,
      gameplay: this.gameplay,
      bounds: this.bounds,
      aircraft: this.aircraft,
      elapsed: this.elapsed,
    };
  }

  getRunwayStatuses(): RunwayStatus[] {
    const context = this.departureContext();
    return this.map.runways.map((runway) => ({
      runwayId: runway.id,
      color: runway.color,
      nextDepartureIn: this.departures.nextDepartureIn(runway.id, context),
      departureQueue: this.departureQueueFor(runway.id).length,
    }));
  }

  totalOccupied(): number {
    let total = 0;
    for (const slot of this.traffic.values()) {
      total += slot.occupied;
    }
    return total;
  }

  totalReservations(): number {
    return this.aircraft.filter(
      (aircraft) => aircraft.target && aircraft.state === "flying",
    ).length;
  }

  departureQueueFor(runwayId: string): Aircraft[] {
    return this.departures.queueFor(runwayId, this.aircraft);
  }

  private queueDeparture(aircraft: Aircraft): void {
    this.departures.queue(aircraft, this.departureContext());
  }

  private departureIsDue(aircraft: Aircraft): boolean {
    return this.departures.isDue(aircraft, this.elapsed);
  }

  /** Scoring, counters and the event are the session's half of a take-off. */
  private tryLaunchDeparture(aircraft: Aircraft): void {
    const runway = this.departures.tryLaunch(aircraft, this.departureContext());
    if (!runway) {
      return;
    }

    const slot = this.traffic.get(runway.color);
    if (slot) {
      slot.occupied = Math.max(0, slot.occupied - 1);
    }

    const remaining = this.totalOccupied();
    const award = this.score.creditDeparture(this.airborneCount());
    this.events.push({
      type: "takeoff",
      position: clonePoint(runway.approach),
      color: runway.color,
      heading: Math.atan2(
        runway.end.y - runway.approach.y,
        runway.end.x - runway.approach.x,
      ),
      total: award.total,
      points: award.points,
      remaining,
    });
  }

  // ------------------------------------------------------------------ output

  drainEvents(): GameEvent[] {
    const drained = this.events;
    this.events = [];
    return drained;
  }

  /** Aircraft in the air right now — the scoring's measure of how busy it is. */
  private airborneCount(): number {
    let count = 0;
    for (const aircraft of this.aircraft) {
      if (aircraft.state === "flying") {
        count++;
      }
    }
    return count;
  }

  snapshot(): GameSnapshot {
    return Object.freeze({
      phase: this.phase,
      elapsedSeconds: this.elapsed,
      score: this.score.snapshot(this.elapsed),
      runwayStatuses: Object.freeze(this.getRunwayStatuses()),
      traffic: Object.freeze({
        occupied: this.totalOccupied(),
        reserved: this.totalReservations(),
      }),
      airborneCount: this.airborneCount(),
    });
  }
}
